/**
 * The app's route tree, gated behind {@link AuthGuard}. Unauthenticated users never reach a route —
 * they see the Sign in screen. Authenticated users get the {@link Layout} shell with the checks
 * list at `/` and detail routes for a check (history/trends + per-location), a result (with a trace
 * link), and a trace.
 *
 * The Granular_CRUD_API adds the write routes (Feature: granular-crud-api, task 14.1; Requirements
 * 11.1, 11.4): Check create/edit, and the Alert_Rule list with its create/edit pages. Every write
 * page renders read-only for a viewer (Req 11.7) — the role gate is inside the page, so a viewer who
 * navigates to a write URL directly still gets a read-only view rather than a broken one, and the
 * API's role matrix remains the enforcement point.
 *
 * The AI_Check_Generation feature adds the generation flow (Feature: ai-check-generation, task 23.6;
 * Requirements 9.1, 9.8): `checks/generate` for the three-mode submission, `check-drafts` for the
 * Account's Draft_Checks with state and expiry, and `check-drafts/:id` for the review form. As with
 * every write page the viewer read-only gate lives inside the page, not on the route.
 *
 * User and data management adds `members` (Feature: user-and-data-management; Requirements 5, 6, 7).
 * It is the one page that mixes a read every role may perform with writes only some may: who can see
 * the Account's data is not a privileged question (Requirement 6's premise), so the whole page renders
 * for a viewer and only its write affordances are gated.
 *
 * The same feature adds `profile` (Requirements 3, 4, 8.1), which is the one page with **no role gate at
 * all**: a person's own name, password, and personal-data export are not Account-scoped privileges, so a
 * viewer uses every control on it. It is reached from the header banner's user panel — the "Manage Profile"
 * link inside that popover (Req 3.1) — rather than from the main nav, because the nav lists the Account's
 * monitoring surfaces and this one describes the person.
 *
 * The Terraform_Provider feature adds `api-keys` (Feature: terraform-provider; Requirement 11), which
 * inverts the Members page's shape rather than copying it: `api-key:read` is granted to admin alone, so a
 * viewer or editor cannot list keys at all, and the page's gate therefore withholds the `GET` as well as
 * the write affordances instead of gating only the latter.
 *
 * Customer-managed inference adds `inference` (Feature: customer-managed-inference; Requirements 13.4,
 * 13.5), on that same inverted shape: every `inference-config:*` action is admin-only, and the page's
 * read carries the External_Id and the whole Published_Role_Policy, so withholding the request for a
 * non-admin is also the narrower disclosure.
 */

import { Route, Routes } from 'react-router-dom';

import { AccountProvider } from './account-context';
import { AuthGuard } from './auth/AuthGuard';
import { Layout } from './components/Layout';
import { AlertRuleCreatePage } from './pages/AlertRuleCreatePage';
import { AlertRuleEditPage } from './pages/AlertRuleEditPage';
import { AlertRulesListPage } from './pages/AlertRulesListPage';
import { ApiKeysPage } from './pages/ApiKeysPage';
import { ChecksListPage } from './pages/ChecksListPage';
import { CheckCreatePage } from './pages/CheckCreatePage';
import { CheckDetailPage } from './pages/CheckDetailPage';
import { CheckDraftReviewPage } from './pages/CheckDraftReviewPage';
import { CheckDraftsListPage } from './pages/CheckDraftsListPage';
import { CheckEditPage } from './pages/CheckEditPage';
import { CheckGeneratePage } from './pages/CheckGeneratePage';
import { InferenceConfigurationPage } from './pages/InferenceConfigurationPage';
import { MembersPage } from './pages/MembersPage';
import { ProfilePage } from './pages/ProfilePage';
import { ResultDetailPage } from './pages/ResultDetailPage';
import { TraceDetailPage } from './pages/TraceDetailPage';

export function App(): JSX.Element {
  return (
    <AuthGuard>
      {/*
        Inside `AuthGuard` because listing the User's Accounts needs the token, and outside the routes
        because every page's API client reads the selection from it (user-and-data-management Req 5.8).
      */}
      <AccountProvider>
        <Routes>
          <Route element={<Layout />}>
            <Route index element={<ChecksListPage />} />
            {/* `checks/new` and `checks/generate` precede `checks/:id` so a literal segment is not
                read as an id. */}
            <Route path="checks/new" element={<CheckCreatePage />} />
            <Route path="checks/generate" element={<CheckGeneratePage />} />
            <Route path="checks/:id" element={<CheckDetailPage />} />
            <Route path="checks/:id/edit" element={<CheckEditPage />} />
            <Route path="check-drafts" element={<CheckDraftsListPage />} />
            <Route path="check-drafts/:id" element={<CheckDraftReviewPage />} />
            <Route path="alert-rules" element={<AlertRulesListPage />} />
            <Route path="alert-rules/new" element={<AlertRuleCreatePage />} />
            <Route path="alert-rules/:id/edit" element={<AlertRuleEditPage />} />
            <Route path="members" element={<MembersPage />} />
            {/* The Account's API keys (Feature: terraform-provider; Requirement 11). The one page whose
                *read* is admin-only — `api-key:read` is granted to admin alone — so unlike every other
                write page the gate inside it withholds the request as well as the affordances, and a
                non-admin gets an explanation rather than a `403`. The route itself stays ungated, for
                the same reason all of them are: the API is the enforcement point. */}
            <Route path="api-keys" element={<ApiKeysPage />} />
            {/* Where model invocations run, and whose bill they land on (Feature:
                customer-managed-inference; Requirements 13.4, 13.5). Admin-only in the same shape as
                `api-keys`: every `inference-config:*` action is granted to admin alone, so the gate
                inside the page withholds both `GET`s as well as the affordances and a non-admin gets an
                explanation rather than a `403`. The route stays ungated, like all of them. */}
            <Route path="inference" element={<InferenceConfigurationPage />} />
            {/* The signed-in User's own profile (Requirements 3, 4, 8.1). Reached from "Manage Profile" in
                the header banner's user panel (Req 3.1), and deliberately NOT in the main nav: it is about
                the person rather than about the Account's monitoring, so it belongs beside the identity it
                describes. */}
            <Route path="profile" element={<ProfilePage />} />
            <Route path="results/:resultId" element={<ResultDetailPage />} />
            <Route path="traces/:traceId" element={<TraceDetailPage />} />
          </Route>
        </Routes>
      </AccountProvider>
    </AuthGuard>
  );
}
